import React from "react";
import { notification } from "antd";

interface PollingOptions {
  pollInterval: number;
  timeout: number;
}

interface DownloadCallbacks {
  onSuccess: (response: any) => void;
  onError: () => void;
  onTimeout: () => void;
  onCleanup: (id: string, notificationKey: string) => void;
}

export enum DownloadStatus {
  STARTED = "STARTED",
  RUNNING = "RUNNING",
  FINISHED = "FINISHED",
  FAILED = "FAILED",
}

export interface DownloadResponse {
  status: DownloadStatus;
  link?: string;
  name: string;
  message?: string;
}

class DownloadPollingService {
  private pollIntervals: Record<string, number> = {};
  private timeouts: Record<string, number> = {};

  async initiateDownload(
    id: string,
    downloadQueryTrigger: any,
    options: PollingOptions,
    callbacks: DownloadCallbacks,
    notificationConfig?: {
      message: string;
      description: React.ReactNode;
    },
  ): Promise<void> {
    try {
      const response = await downloadQueryTrigger(id).unwrap();

      if (response.status === DownloadStatus.FINISHED && response.link) {
        // Immediate download
        this.downloadFile(response.link, response.name);
        callbacks.onSuccess(response);
      } else if (
        response.status === DownloadStatus.STARTED ||
        response.status === DownloadStatus.RUNNING
      ) {
        // Start polling
        this.startPolling(
          id,
          downloadQueryTrigger,
          options,
          callbacks,
          notificationConfig,
        );
      }
    } catch {
      callbacks.onError();
    }
  }

  private startPolling(
    id: string,
    downloadQueryTrigger: any,
    options: PollingOptions,
    callbacks: DownloadCallbacks,
    notificationConfig?: {
      message: string;
      description: React.ReactNode;
    },
  ): void {
    this.stopPolling(id);

    const { pollInterval, timeout } = options;
    const uniqueNotificationKey = `download-${Math.floor(Math.random() * 10).toString()}`;

    // Show persistent notification if config provided
    if (notificationConfig) {
      notification.info({
        message: notificationConfig.message,
        description: notificationConfig.description,
        duration: 0,
        closable: false,
        key: uniqueNotificationKey,
        placement: "bottomRight",
      });
    }

    // Start polling
    this.pollIntervals[id] = window.setInterval(async () => {
      try {
        const response = await downloadQueryTrigger(id).unwrap();

        if (response.status === DownloadStatus.FINISHED && response.link) {
          this.handleSuccess(id, response, uniqueNotificationKey, callbacks);
        } else if (response.status === DownloadStatus.FAILED) {
          this.handleError(id, uniqueNotificationKey, callbacks);
        }
        // Continue polling for STARTED/RUNNING
      } catch {
        this.handleError(id, uniqueNotificationKey, callbacks);
      }
    }, pollInterval);

    // Set timeout
    this.timeouts[id] = window.setTimeout(() => {
      this.handleTimeout(id, uniqueNotificationKey, callbacks);
    }, timeout);
  }

  private handleSuccess(
    id: string,
    response: DownloadResponse,
    notificationKey: string,
    callbacks: DownloadCallbacks,
  ): void {
    this.cleanup(id, notificationKey);
    this.downloadFile(response.link as string, response.name);
    callbacks.onSuccess(response);
    callbacks.onCleanup(id, notificationKey);
  }

  private handleError(
    id: string,
    notificationKey: string,
    callbacks: DownloadCallbacks,
  ): void {
    this.cleanup(id, notificationKey);
    callbacks.onError();
    callbacks.onCleanup(id, notificationKey);
  }

  private handleTimeout(
    id: string,
    notificationKey: string,
    callbacks: DownloadCallbacks,
  ): void {
    this.cleanup(id, notificationKey);
    callbacks.onTimeout();
    callbacks.onCleanup(id, notificationKey);
  }

  private cleanup(id: string, notificationKey: string): void {
    if (this.pollIntervals[id]) {
      clearInterval(this.pollIntervals[id]);
      delete this.pollIntervals[id];
    }

    if (this.timeouts[id]) {
      clearTimeout(this.timeouts[id]);
      delete this.timeouts[id];
    }

    notification.destroy(notificationKey);
  }

  private downloadFile(url: string, filename: string): void {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "download";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  stopPolling(id: string): void {
    if (this.pollIntervals[id]) {
      clearInterval(this.pollIntervals[id]);
      delete this.pollIntervals[id];
    }

    if (this.timeouts[id]) {
      clearTimeout(this.timeouts[id]);
      delete this.timeouts[id];
    }
  }

  isPolling(id: string): boolean {
    return !!this.pollIntervals[id];
  }

  cleanupAll(): void {
    Object.values(this.pollIntervals).forEach(clearInterval);
    Object.values(this.timeouts).forEach(clearTimeout);
    this.pollIntervals = {};
    this.timeouts = {};
  }
}

export const downloadPollingService = new DownloadPollingService();
